/**
 * Lokales Speichern des Analysezustands (localStorage) mit Schema-Version.
 * Beschädigte oder inkompatible Daten werden nicht geladen, sondern gesichert
 * und gemeldet.
 */
import { z } from 'zod';
import { STATE_SCHEMA_VERSION, type AnalysisConfig, type Building, type MetricKey, type Placement } from '../core/domain/types';
import { defaultConfig, sanitizeConfig } from '../core/domain/config';
import type { DataMode } from '../core/worker/protocol';

/** Obergrenzen des gespeicherten Zustands; `addBuildings` hält sie ein, sonst wäre der Zustand beim nächsten Start ungültig. */
export const MAX_BUILDINGS = 2000;
export const MAX_LABEL = 300;

const elevationSource = z.enum(['swissALTI3D', 'open-meteo-dem90', 'manuell']);
const nn = z.number().nullable();

export const buildingSchema = z.object({
  id: z.string().min(1),
  label: z.string().max(MAX_LABEL),
  address: z.string().optional(),
  egid: z.string().optional(),
  country: z.string().optional(),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  lv95: z.object({ e: z.number(), n: z.number() }).optional(),
  elevation: z.object({ value: z.number(), source: elevationSource }).optional(),
  timezone: z.string().optional(),
  geocoder: z.enum(['geo.admin.ch', 'open-meteo-geocoding', 'koordinaten', 'csv-koordinaten']),
  addedAt: z.string(),
  gwr: z
    .object({
      egid: z.string(),
      gbauj: nn,
      gbaup: nn,
      gkat: nn,
      gklas: nn,
      gstat: nn,
      gastw: nn,
      gebf: nn,
      garea: nn,
      gwaerzh1: nn,
      genh1: nn,
      gwaerzw1: nn,
      genw1: nn,
      gexpdat: z.string().nullable(),
      fetchedAt: z.string(),
    })
    .optional(),
});

export const placementSchema = z.object({
  buildingId: z.string(),
  model: z.enum(['era5_land', 'era5']),
  cellKey: z.string(),
  cellLat: z.number(),
  cellLon: z.number(),
  timezone: z.string(),
  targetElevation: z.number(),
  targetElevationSource: elevationSource,
  discoveredAt: z.string(),
});

const hintsSchema = z.object({
  recentYears: z.number(),
  cddRecentMin: z.number(),
  hotDaysRecentMin: z.number(),
  coolShareMin: z.number(),
  warmNightsRecentMin: z.number(),
  heatLongestMin: z.number(),
  hddDropMin: z.number(),
  cddRiseMin: z.number(),
});

export const configSchema = z.object({
  model: z.enum(['era5_land', 'era5']),
  heightCorrection: z.boolean(),
  hddBase: z.number(),
  cddBase: z.number(),
  hotDayThreshold: z.number(),
  heatPeriodMinDays: z.number(),
  nightStartHour: z.number(),
  nightEndHour: z.number(),
  warmNightThreshold: z.number(),
  coolingRef: z.number(),
  coolingMinDelta: z.number(),
  coolingSeasonStartMonth: z.number(),
  coolingSeasonEndMonth: z.number(),
  referenceStart: z.number(),
  referenceEnd: z.number(),
  periodStart: z.number(),
  periodEnd: z.number(),
  hints: hintsSchema,
});

const metricKey = z.enum(['hdd', 'cdd', 'warmNights', 'coolHours', 'coolKh', 'hotDays', 'heatEvents', 'heatDays', 'heatLongest', 'tMean', 'tropicalNights', 'veryHotDays', 'txx']);

export const persistedSchema = z.object({
  schemaVersion: z.literal(STATE_SCHEMA_VERSION),
  buildings: z.array(buildingSchema).max(MAX_BUILDINGS),
  placements: z.array(placementSchema),
  config: configSchema,
  focusId: z.string().nullable(),
  compareIds: z.array(z.string()).max(5),
  metric: metricKey,
  detailYear: z.number().nullable(),
  savedAt: z.string(),
});

export interface PersistedState {
  schemaVersion: typeof STATE_SCHEMA_VERSION;
  buildings: Building[];
  placements: Placement[];
  config: AnalysisConfig;
  focusId: string | null;
  compareIds: string[];
  metric: MetricKey;
  detailYear: number | null;
  savedAt: string;
}

const key = (mode: DataMode) => `klimaraum.state.v${STATE_SCHEMA_VERSION}.${mode}`;
export const MODE_KEY = 'klimaraum.mode';

export type LoadResult = { ok: true; state: PersistedState } | { ok: false; reason: 'none' } | { ok: false; reason: 'corrupt' | 'incompatible'; detail: string };

export function parsePersisted(raw: unknown): LoadResult {
  if (raw && typeof raw === 'object' && 'schemaVersion' in raw && (raw as { schemaVersion: unknown }).schemaVersion !== STATE_SCHEMA_VERSION) {
    return { ok: false, reason: 'incompatible', detail: `Schema-Version ${(raw as { schemaVersion: unknown }).schemaVersion} statt ${STATE_SCHEMA_VERSION}` };
  }
  const r = persistedSchema.safeParse(raw);
  if (!r.success) return { ok: false, reason: 'corrupt', detail: r.error.issues.slice(0, 3).map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
  const st = r.data as PersistedState;
  const ids = new Set(st.buildings.map((b) => b.id));
  return {
    ok: true,
    state: {
      ...st,
      config: sanitizeConfig({ ...defaultConfig(), ...st.config }),
      placements: st.placements.filter((p) => ids.has(p.buildingId)),
      focusId: st.focusId && ids.has(st.focusId) ? st.focusId : (st.buildings[0]?.id ?? null),
      compareIds: st.compareIds.filter((id) => ids.has(id)),
    },
  };
}

export function loadState(mode: DataMode): LoadResult {
  let text: string | null = null;
  try {
    text = localStorage.getItem(key(mode));
  } catch {
    return { ok: false, reason: 'none' };
  }
  if (text === null) return { ok: false, reason: 'none' };
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    backupCorrupt(mode, text);
    return { ok: false, reason: 'corrupt', detail: 'Kein gültiges JSON' };
  }
  const r = parsePersisted(raw);
  if (!r.ok && r.reason !== 'none') backupCorrupt(mode, text);
  return r;
}

function backupCorrupt(mode: DataMode, text: string) {
  try {
    localStorage.setItem(`${key(mode)}.beschaedigt.${Date.now()}`, text);
  } catch {
    /* Speicher voll: Sicherung nicht möglich */
  }
}

export function saveState(mode: DataMode, st: PersistedState): boolean {
  try {
    localStorage.setItem(key(mode), JSON.stringify(st));
    return true;
  } catch {
    return false;
  }
}

export function clearState(mode: DataMode) {
  try {
    localStorage.removeItem(key(mode));
  } catch {
    /* ignorieren */
  }
}
